import React, { ReactElement, useState, useContext, useMemo, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch, AiOutlineDownload } from "react-icons/ai";
import { MdOutlineCancel, MdOutlineContentCopy } from "react-icons/md";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import BatchCancelConfirmModal from "./BatchCancelConfirmModal";
import SweetAlert2 from "react-sweetalert2";
import axios from "axios";
import { ProfileContext } from "../../../../store/ProfileProvider";
import DisplayAmount from "../../shared/DisplayAmount";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

ModuleRegistry.registerModules([AllCommunityModule]);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  id: string;
  file_name: string;
  file_bucket: string;
  file_key: string;
  process_date: string;
  processed_at: string;
  merchant: Merchant;
  subaccount: Subaccount;
  records: string;
  amount: string;
  created_at: string;
  status: string;
  provider_name: string;
  provider_account_name: string;
  method_name: string;
  batch_reference: string;
  asset: string;
  crypto: boolean;
};

const BatchList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadBatchList(): void;
  processingBatchIds?: Set<string>;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadBatchList,
  processingBatchIds,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [batchToCancel, setBatchToCancel] = useState<any>(null);
  const [isOpenBatchCancelConfirmModal, setIsOpenBatchCancelConfirmModal] =
    useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [swalProps, setSwalProps] = useState<any>({});
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  interface BatchStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: BatchStatusItems[] = [
    {
      status: "open",
      value: "open",
      display: <Badge className={`${statusClasses} status-open`}>Open</Badge>,
    },
    {
      status: "close",
      value: "close",
      display: (
        <Badge className={`${statusClasses} status-closed`}>Closed</Badge>
      ),
    },
    {
      status: "queue",
      value: "queue",
      display: (
        <Badge className={`${statusClasses} status-queued`}>Queued</Badge>
      ),
    },
    {
      status: "initializing",
      value: "initializing",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Initializing</Badge>
      ),
    },
    {
      status: "processing",
      value: "processing",
      display: (
        <Badge className={`${statusClasses} status-processing`}>Processing</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "in_progress",
      value: "in_progress",
      display: (
        <Badge className={`${statusClasses} status-in_progress`}>
          In Progress
        </Badge>
      ),
    },
    {
      status: "settled",
      value: "settled",
      display: (
        <Badge className={`${statusClasses} status-settled`}>Settled</Badge>
      ),
    },
    {
      status: "settled_with_error",
      value: "settled_with_error",
      display: (
        <Badge className={`${statusClasses} status-settled_with_error`}>
          Settled with Errors
        </Badge>
      ),
    },
    {
      status: "settling",
      value: "settling",
      display: (
        <Badge className={`${statusClasses} status-settling`}>Settling</Badge>
      ),
    },
    {
      status: "cancelling",
      value: "cancelling",
      display: (
        <Badge className={`${statusClasses} status-cancelling`}>Cancelling</Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const formatDate = (timestamp: string, time: boolean = false) => {
    if (timestamp && timestamp !== "") {
      if (time) {
        return dayjs(Number(timestamp))
          .tz(profileTimeZone)
          .format("DD MMM YY - hh:mm A");
      } else {
        return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM YY");
      }
    }
    return "";
  };

  const handleClickCancel = (batch: any) => {
    setBatchToCancel(batch);
    setIsOpenBatchCancelConfirmModal(true);
  };

  const closeBatchCancelConfirmModal = () => {
    setBatchToCancel(null);
    setIsOpenBatchCancelConfirmModal(false);
  };

  const handleClickDownload = async (
    file_bucket: string,
    file_key: string,
    file_name: string
  ) => {
    try {
      setIsDownloading(true);

      const postData = {
        file_bucket: file_bucket,
        file_key: file_key,
      };

      await axios
        .post(
          process.env.REACT_APP_API_URL +
            `/api/${userRole}/batch/action/batch-generate-download-url`,
          postData
        )
        .then(async function (response) {
          if (response && response.data?.status === "ok") {
            const fileUrl = response.data.url;
            const fileResponse = await fetch(fileUrl);
            const blob = await fileResponse.blob();

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = file_name;
            link.click();

            window.URL.revokeObjectURL(link.href);
            setIsDownloading(false);
          } else {
            setIsDownloading(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsDownloading(false);
              },
            });
          }
        })
        .catch(function (error) {
          setIsDownloading(false);
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: error.response.data.message,
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
              setIsDownloading(false);
            },
          });
        });
    } catch (error) {
      setIsDownloading(false);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsDownloading(false);
        },
      });
    }
  };

  const ellipsis_middle = function (str: string, length: number) {
    if (str.length > length * 2) {
      return (
        str.slice(0, length) +
        "..." +
        str.slice(str.length - length, str.length)
      );
    }
    return String(str);
  };

  const handleClickCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  // AG Grid cell renderers
  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const CreatedDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.created_at)}</>
  );

  const ProcessDateRenderer = (params: ICellRendererParams) => (
    <>{formatDate(params.data.processed_at, true)}</>
  );

  const SubaccountRenderer = (params: ICellRendererParams) => (
    <>{params.data.subaccount?.name}</>
  );

  const FileRenderer = (params: ICellRendererParams) => {
    if (
      params.data?.file_bucket &&
      params.data?.file_key &&
      params.data?.file_name
    ) {
      return (
        <Tooltip title="Download" arrow placement="top">
          <div>
            <IconButton
              id={"button-download-batch-file-" + params.data?.id}
              onClick={() =>
                handleClickDownload(
                  params.data.file_bucket,
                  params.data.file_key,
                  params.data.file_name
                )
              }
              size="small"
              disabled={isDownloading}
            >
              <AiOutlineDownload className="h-5 w-5 text-navy-700" />
            </IconButton>
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const ProviderRenderer = (params: ICellRendererParams) => (
    <>
      {params.data.provider_name && params.data.provider_account_name
        ? params.data.provider_name +
          " (" +
          params.data.provider_account_name +
          ")"
        : ""}
    </>
  );

  const MethodRenderer = (params: ICellRendererParams) => (
    <>{params.data.method_name ? params.data.method_name : ""}</>
  );

  const ReferenceRenderer = (params: ICellRendererParams) => {
    if (params.data.batch_reference) {
      return (
        <div className="flex w-full items-center justify-between">
          <span className="truncate">
            {ellipsis_middle(params.data.batch_reference, 10)}
          </span>
          <Tooltip title="Copy Batch Reference" arrow placement="top">
            <div>
              <IconButton
                id={"button-copy-batch-ref-" + params.data?.id}
                onClick={() => handleClickCopy(params.data.batch_reference)}
                size="small"
              >
                <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
              </IconButton>
            </div>
          </Tooltip>
        </div>
      );
    }
    return null;
  };

  const ReferenceFullRenderer = (params: ICellRendererParams) => {
    if (params.data.batch_reference) {
      return (
        <div className="flex w-full items-center justify-between">
          <span className="truncate">{params.data.batch_reference}</span>
          <Tooltip title="Copy Batch Reference" arrow placement="top">
            <div>
              <IconButton
                id={"button-copy-batch-ref-" + params.data?.id}
                onClick={() => handleClickCopy(params.data.batch_reference)}
                size="small"
              >
                <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
              </IconButton>
            </div>
          </Tooltip>
        </div>
      );
    }
    return null;
  };

  const AmountRenderer = (params: ICellRendererParams) =>
    params.data.crypto ? (
      <>{params.data.amount}</>
    ) : (
      <DisplayAmount
        amount={Number(params.data.amount)}
        currency={params.data.asset}
      />
    );

  const StatusRenderer = (params: ICellRendererParams) => {
    const statusItem = statusList.find((item) => item.status === params.data.status);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const canCancel =
      (params.data?.status === "open" ||
        ((params.data?.status === "queue" ||
          params.data?.status === "close") &&
          userRole === "superadmin")) &&
      userPermissions?.cancelBatch;

    return (
      <div className="flex items-center justify-end">
        {canCancel && (
          <Tooltip title="Cancel" arrow placement="top">
            <div>
              <IconButton
                id={"button-cancel-" + params.data?.id}
                disabled={isDownloading}
                onClick={() => handleClickCancel(params.data)}
                size="small"
              >
                <MdOutlineCancel className="status-cancelled h-5 w-5" />
              </IconButton>
            </div>
          </Tooltip>
        )}
        <Tooltip title="View More Details" arrow placement="top">
          <div>
            <IconButton
              component={Link}
              to={`/${userRole}/batch/${params.data.id}`}
              id={"button-detail-" + params.data?.id}
              disabled={isDownloading}
              size="small"
            >
              <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
            </IconButton>
          </div>
        </Tooltip>
      </div>
    );
  };

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      width: 110,
      cellRenderer: CreatedDateRenderer,
    },
    {
      field: "processed_at",
      headerName: "Process Date",
      sortable: false,
      minWidth: 160,
      flex: 1,
      cellRenderer: ProcessDateRenderer,
    },
    {
      field: "subaccount",
      headerName: "",
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: SubaccountRenderer,
    },
    {
      field: "file_name",
      headerName: "File",
      sortable: false,
      width: 70,
      cellRenderer: FileRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "provider_name",
      headerName: "Provider",
      sortable: false,
      minWidth: 140,
      flex: 1.5,
      cellRenderer: ProviderRenderer,
    },
    {
      field: "method_name",
      headerName: "Method",
      sortable: false,
      minWidth: 80,
      flex: 1,
      cellRenderer: MethodRenderer,
    },
    {
      field: "batch_reference",
      headerName: "Reference",
      sortable: false,
      minWidth: 140,
      flex: 1,
      cellRenderer: ReferenceRenderer,
    },
    {
      field: "record",
      headerName: "Record(s)",
      sortable: false,
      width: 90,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "amount",
      headerName: "Amount",
      sortable: false,
      width: 100,
      cellRenderer: AmountRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      headerClass: "ag-right-aligned-header",
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      width: 70,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      width: 150,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 100,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const filterColumns = useCallback(() => {
    let rebuildColumns = [...columns];
    if (userRole === "superadmin") {
      return rebuildColumns;
    } else if (userRole === "merchant") {
      const excludeFields = ["file_name", "provider_name", "method_name"];
      rebuildColumns = rebuildColumns.filter(
        (column) => !excludeFields.includes(column.field!)
      );
    } else {
      const excludeFields = [
        "subaccount",
        "file_name",
        "provider_name",
        "method_name",
      ];
      rebuildColumns = rebuildColumns.filter(
        (column) => !excludeFields.includes(column.field!)
      );
    }
    // Non-superadmin: fix date widths and show full batch reference
    const createdCol = rebuildColumns.find((c) => c.field === "created_at");
    if (createdCol) {
      createdCol.width = 140;
    }
    const processCol = rebuildColumns.find((c) => c.field === "processed_at");
    if (processCol) {
      processCol.width = 160;
      delete processCol.minWidth;
      delete processCol.flex;
    }
    const refCol = rebuildColumns.find((c) => c.field === "batch_reference");
    if (refCol) {
      refCol.cellRenderer = ReferenceFullRenderer;
    }
    return rebuildColumns;
  }, [userRole, userPermissions, processingBatchIds]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <>
      {isOpenBatchCancelConfirmModal && (
        <BatchCancelConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadBatchList}
          isOpen={isOpenBatchCancelConfirmModal}
          batchToCancel={batchToCancel}
          onClose={closeBatchCancelConfirmModal}
        />
      )}
      <Box className="card w-full">
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={filterColumns()}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default BatchList;
